import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Dialog, withStyles, Tooltip } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import { I18n } from 'aws-amplify';
import { Storage } from 'aws-amplify';

ViewImage.propTypes = {
    imageData: PropTypes.object
};

function ViewImage({ imageData }) {
    const [openPreview, setOpenPreview] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [isImageError, setIsImageError] = useState(false);
    const [isShowFlagMessage, setIsShowFlagMessage] = useState(false);

    const handleImageLoad = () => setIsImageLoaded(true);
    const handleImageError = () => setIsImageError(true);

    const FlagMessageCopyLink = withStyles({
        tooltip: {
            color: '#1DA578',
            backgroundColor: '#D7F4E2',
            padding: '8px 16px',
            fontSize: '14px',
            boxShadow: '6px 5px 5px -4px rgba(0,0,0,0.3)'
        },
        arrow: {
            color: '#D7F4E2'
        }
    })(Tooltip);

    const downloadImage = useCallback(async () => {
        const imageUrl = imageData?.localUrl;

        try {
            const response = await fetch(imageUrl, { mode: 'cors' });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            const urlPath = imageUrl.split("?")[0];
            const extMatch = urlPath.match(/\.[a-zA-Z0-9]+$/);
            const ext = extMatch ? extMatch[0] : ".jpg";

            // --- fileName: random string ---
            const randomName = Math.random().toString(36).substring(2, 10);

            const fileName = `${randomName}${ext}`;
            link.setAttribute('download', fileName);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('downloadImage error :', error);
        }
    }, [imageData]);

    const copyLinkImage = useCallback(async () => {
        try {
            let localUrl = imageData.localUrl;
            if (localUrl.startsWith("blob")) {
                let s3Path = imageData?.s3Path || '';
                if (s3Path.startsWith('public/')) {
                    s3Path = s3Path.slice(7);
                }
                localUrl = await Storage.get(s3Path, { level: 'public' });
            }
            await navigator.clipboard.writeText(localUrl);

            setIsShowFlagMessage(true);

            setTimeout(() => {
                setIsShowFlagMessage(false);
            }, 1000);

        } catch (err) {
            console.log('copyLinkImage err :', err);
        }
    }, [imageData]);

    if (!imageData?.localUrl) {
        return (
            <div
                className="ai-image-skeleton"
                style={{
                    minWidth: 145,
                    minHeight: 145,
                    borderRadius: 8,
                    overflow: "hidden",
                    position: "relative"
                }}
            >
                <div className="shimmer"></div>
            </div>
        );
    }

    return (
        <>
            {isImageError ? (
                <div className="ai-chat-msg-attachment-img-wrapper">
                    <img
                        className="ai-chat-view-image"
                        alt="preview-img-error"
                        src={imageData.localUrl}
                    />
                </div>
            ) : (
                <div className="ai-chat-msg-attachment-img-wrapper">
                    <button
                        className="btn-reset-default ai-chat-msg-attachment-img"
                        onClick={() => setOpenPreview(true)}
                    >
                        <img
                            className="ai-chat-view-image"
                            alt="preview-img"
                            src={imageData.localUrl}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            style={{
                                opacity: isImageLoaded ? 1 : 0.5,
                                filter: isImageLoaded ? 'none' : 'blur(2px)'
                            }}
                        />
                    </button>
                    <div className="ai-chat-view-image-options">
                        <Tooltip title={I18n.get('DOWNLOAD')} placement="top" arrow>
                            <button
                                className="btn-reset-default image-option-item"
                                onClick={downloadImage}
                            >
                                <DownloadFile />
                            </button>
                        </Tooltip>
                        <Tooltip title={I18n.get('COPY_URL')} placement="top" arrow>
                            <button
                                className="btn-reset-default image-option-item"
                                onClick={copyLinkImage}
                            >
                                <FlagMessageCopyLink
                                    title={I18n.get('KNOWDEMSG002')}
                                    placement="top-start"
                                    arrow={true}
                                    open={isShowFlagMessage}
                                >
                                    <LinkIcon className="ai-img-copy-link-icon" />
                                </FlagMessageCopyLink>
                            </button>
                        </Tooltip>
                    </div>
                </div>
            )}

            <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md">
                <img
                    alt="preview-full"
                    src={imageData.localUrl}
                    style={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        objectFit: 'contain'
                    }}
                />
            </Dialog>
        </>
    );
}

export default ViewImage;

const DownloadFile = () => {
    return (
        <svg
            width="17"
            height="17"
            viewBox="0 0 20 20"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            className="icon fill-current"
            style={{
                filter: "drop-shadow(rgba(0, 0, 0, 0.15) 0px 2px 4px)"
            }}
        >
            <path d="M2.66821 12.6663V12.5003C2.66821 12.1331 2.96598 11.8353 3.33325 11.8353C3.70052 11.8353 3.99829 12.1331 3.99829 12.5003V12.6663C3.99829 13.3772 3.9992 13.8707 4.03052 14.2542C4.0612 14.6298 4.11803 14.8413 4.19849 14.9993L4.2688 15.1263C4.44511 15.4137 4.69813 15.6481 5.00024 15.8021L5.13013 15.8577C5.2739 15.9092 5.46341 15.947 5.74536 15.97C6.12888 16.0014 6.62221 16.0013 7.33325 16.0013H12.6663C13.3771 16.0013 13.8707 16.0014 14.2542 15.97C14.6295 15.9394 14.8413 15.8825 14.9993 15.8021L15.1262 15.7308C15.4136 15.5545 15.6481 15.3014 15.802 14.9993L15.8577 14.8695C15.9091 14.7257 15.9469 14.536 15.97 14.2542C16.0013 13.8707 16.0012 13.3772 16.0012 12.6663V12.5003C16.0012 12.1332 16.2991 11.8355 16.6663 11.8353C17.0335 11.8353 17.3313 12.1331 17.3313 12.5003V12.6663C17.3313 13.3553 17.3319 13.9124 17.2952 14.3626C17.2624 14.7636 17.1974 15.1247 17.053 15.4613L16.9866 15.6038C16.7211 16.1248 16.3172 16.5605 15.8215 16.8646L15.6038 16.9866C15.227 17.1786 14.8206 17.2578 14.3625 17.2952C13.9123 17.332 13.3553 17.3314 12.6663 17.3314H7.33325C6.64416 17.3314 6.0872 17.332 5.63696 17.2952C5.23642 17.2625 4.87552 17.1982 4.53931 17.054L4.39673 16.9866C3.87561 16.7211 3.43911 16.3174 3.13501 15.8216L3.01294 15.6038C2.82097 15.2271 2.74177 14.8206 2.70435 14.3626C2.66758 13.9124 2.66821 13.3553 2.66821 12.6663ZM9.33521 3.33333C9.33521 2.96606 9.63298 2.66829 10.0002 2.66829C10.3674 2.66847 10.6653 2.96617 10.6653 3.33333V10.8939L12.8625 8.69661L12.967 8.61165C13.2252 8.44092 13.5766 8.46925 13.804 8.69661C14.0633 8.95628 14.0634 9.37744 13.804 9.63704L10.47 12.97C10.3453 13.0947 10.1765 13.1653 10.0002 13.1654C9.82388 13.1654 9.65425 13.0948 9.52954 12.97L6.19653 9.63704L6.11157 9.53255C5.94101 9.27441 5.96924 8.9239 6.19653 8.69661C6.42382 8.46932 6.77433 8.44109 7.03247 8.61165L7.13696 8.69661L9.33521 10.8949V3.33333Z"></path>
        </svg>
    );
};
